/**
 * Checkpoint: a sensor flag the player passes through. The most recently
 * triggered checkpoint becomes the respawn point on death (instead of the
 * level's `spawn`).
 *
 * Visual: a small mast with a flag — gray pennant when inactive, glowing
 * purple/pink pennant when armed. The activation pop is a quick scale punch
 * driven by the scene's frame loop (we keep the entity stateless about time
 * and just expose `triggered` + `triggerPulse` for the renderer).
 */

import Matter from 'matter-js';
import { Container, Graphics } from 'pixi.js';

export interface CheckpointOptions {
  x: number;
  y: number;
  /** Optional launch velocity applied on respawn from this checkpoint. */
  vx?: number;
  vy?: number;
}

const POLE_H = 60;
const POLE_W = 4;
const FLAG_W = 28;
const FLAG_H = 18;
const RADIUS = 22; // sensor radius for "passed through" detection

export class Checkpoint {
  readonly body: Matter.Body;
  readonly view: Container;
  readonly x: number;
  readonly y: number;
  readonly vx: number;
  readonly vy: number;
  triggered = false;
  /** Seconds since trigger; used by the scene to drive a brief flag pop. */
  triggeredAtSec = -1;

  private readonly flag: Graphics;
  private readonly halo: Graphics;

  constructor(opts: CheckpointOptions) {
    this.x = opts.x;
    this.y = opts.y;
    this.vx = opts.vx ?? 0;
    this.vy = opts.vy ?? 0;

    this.body = Matter.Bodies.circle(this.x, this.y - POLE_H / 2, RADIUS, {
      isStatic: true,
      isSensor: true,
      label: 'checkpoint',
    });

    const c = new Container();
    c.x = this.x;
    c.y = this.y;

    // Soft halo that fades in when armed.
    const halo = new Graphics();
    halo.circle(0, -POLE_H / 2, RADIUS + 4).fill({ color: 0xaa77dd, alpha: 0 });
    c.addChild(halo);
    this.halo = halo;

    // Pole.
    const pole = new Graphics();
    pole.rect(-POLE_W / 2, -POLE_H, POLE_W, POLE_H)
      .fill(0xeeeeee)
      .stroke({ color: 0x000000, width: 1, alpha: 0.45 });
    c.addChild(pole);

    // Flag (drawn from the pole top, pointing right with a triangular notch).
    const flag = new Graphics();
    this.flag = flag;
    this.redrawFlag(false);
    flag.x = POLE_W / 2;
    flag.y = -POLE_H + 2;
    c.addChild(flag);

    // Base.
    const base = new Graphics();
    base.ellipse(0, 0, 10, 3).fill({ color: 0x000000, alpha: 0.5 });
    c.addChild(base);

    this.view = c;
  }

  /** Marks this checkpoint as armed. Idempotent. */
  trigger(nowSec: number): boolean {
    if (this.triggered) return false;
    this.triggered = true;
    this.triggeredAtSec = nowSec;
    this.redrawFlag(true);
    return true;
  }

  /** Per-frame visual update for the activation pop + halo pulse. */
  render(nowSec: number): void {
    if (!this.triggered) return;
    const dt = nowSec - this.triggeredAtSec;
    // 0.6s scale punch on the flag.
    if (dt < 0.6) {
      const k = 1 + Math.sin((dt / 0.6) * Math.PI) * 0.35;
      this.flag.scale.set(k, k);
    } else {
      this.flag.scale.set(1, 1);
    }
    // Steady gentle pulse on the halo.
    const pulse = 0.18 + 0.12 * (0.5 + 0.5 * Math.sin(nowSec * 3.0));
    this.halo.alpha = pulse;
  }

  private redrawFlag(armed: boolean): void {
    const fill = armed ? 0xff6dd0 : 0x666677;
    const stroke = armed
      ? { color: 0xffffff, width: 1.5, alpha: 0.95 }
      : { color: 0xffffff, width: 1, alpha: 0.5 };
    this.flag.clear();
    // Pennant: rectangle with a triangular notch on the right.
    this.flag
      .moveTo(0, 0)
      .lineTo(FLAG_W, 0)
      .lineTo(FLAG_W - 8, FLAG_H / 2)
      .lineTo(FLAG_W, FLAG_H)
      .lineTo(0, FLAG_H)
      .closePath()
      .fill(fill)
      .stroke(stroke);
  }
}
